/* eslint-disable @nx/enforce-module-boundaries */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Paper, Typography, Modal, IconButton, useMediaQuery, Button } from '@mui/material';
import { useTheme } from '@mui/system';
import CustomIconButton from 'packages/novous_web/src/common/components/CustomIconButton';
import refresh from 'packages/novous_web/src/assets/icons/Refresh.svg';
import CloseIcon from 'packages/novous_web/src/assets/icons/Close Square.svg';
import share from 'packages/novous_web/src/assets/icons/ShareWhite.svg';
import VideoPlayer from '../../Trips/componenets/VideoPlayer';
import { API_ENDPOINTS } from 'packages/novous_web/src/api/endpoints';
import useAxios from 'packages/novous_web/src/api/useAxios';
import CameraButton from '../../Trips/componenets/CameraButton';
import { multiSectionDigitalClockSectionClasses } from '@mui/x-date-pickers';
import MP4VideoPlayer from '../../Trips/componenets/Mp4VideoPlayer';
import CallingModal from 'packages/novous_web/src/common/components/CallingModal';
import ShareFootageModal from 'packages/novous_web/src/common/components/ShareFootageModal';
import AlertVideoPlayer from '../../Trips/componenets/AlertVideoPlayer';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { useAuth } from 'packages/novous_web/src/authContext/useAuth';
import { useNavigate } from 'react-router-dom';
import reverse from 'packages/novous_web/src/assets/icons/Fast Backward.svg';
import SockJS from 'sockjs-client';
import { Client } from '@stomp/stompjs';

type LiveFeedModalProps = {
  open: boolean;
  trip: any;
  selectedCameraName?: string;
  selectedCamera?: number;
  cameraInfo?: CameraProps[];
  type?: string;
  isDash?: boolean;
  alertName?: string;

  onClose: () => void;
  vehicleId?: string;
  deviceId?: string;
  deviceType?: string;
  isCheckingVideos?: boolean;
  isDriverCoachingVideos?: boolean;
  runningHistory?: boolean;
};

interface CameraProps {
  cameraId: string;
  cameraName: string;
  playBackUrl: string;
  gForce?: [];
  alertTime?: string;
  alertData?: any;
  error?: string;
  message?: string;
}

const LiveFeedModal: React.FC<LiveFeedModalProps> = ({
  open,
  selectedCameraName = '',
  isDash = false,
  selectedCamera = 0,
  alertName = '',
  cameraInfo = [],
  type = 'live',
  onClose,
  vehicleId,
  trip,
  deviceId,
  isCheckingVideos,
  isDriverCoachingVideos,
  deviceType,
  runningHistory = true,
}) => {
  const { user } = useAuth();
  const theme = useTheme();
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef(true);
  const [isDeviceOffline, setDeviceOffline] = useState({ show: false, message: '' });
  const [totalVideos, setTotalVideos] = useState<CameraProps[]>([]);
  const [liveVideo, setLiveVideo] = useState<CameraProps[]>([]);
  const [selected, setSelected] = useState(selectedCamera);
  const [dimension, setDimension] = useState({ rowHeight: 0, rowWidth: 0 });
  const [callModalOpen, setCallModalOpen] = useState(false);
  const prevData = useRef(0);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [windowFullScreen, setWindowFullScreen] = useState(false);
  const [Activelist, setActiveList] = useState<CameraProps[][]>([]);
  const [fullModee, setFullModee] = useState(false);
  const [singleFullMode, setSingleFullMode] = useState(false);
  const countRef = useRef(0);

  const fetchStartTimeRef = useRef<number>(0);
  const maxFetchDuration = 120000; // 2 minutes in milliseconds
  const [fetchTimedOut, setFetchTimedOut] = useState(false);

  // Auto-close timer refs and state
  const autoCloseTimerRef = useRef<NodeJS.Timeout | null>(null);
  const liveStreamOpenTimeRef = useRef<number>(0);
  const AUTO_CLOSE_DURATION = 2 * 60 * 1000; // 2 minutes in milliseconds

  useEffect(() => {
    setSelected(selectedCamera);
  }, [selectedCamera]);

  // Cleanup effect: Clear all states when modal is closed
  useEffect(() => {
    if (!open) {
      setTotalVideos([]);
      setLiveVideo([]);
      prevData.current = 0;
      setDeviceOffline({ show: false, message: '' });
      setNetworkStrength('NA');
    }
  }, [open]);

  const [networkStrength, setNetworkStrength] = useState<string>('NA');

  useEffect(() => {
    if (open && trip && type === 'live' && user?.id !== '685e1eec79b388464c5cd08e') {
      const currentVehicleId = trip?.vehicleRegNo || trip?.vehicleId;

      if (!currentVehicleId) return;

      const socket = new SockJS(`${process.env.NX_PUBLIC_API_URL}/ws`);
      const stompClient = new Client({
        webSocketFactory: () => socket,
        onConnect: () => {
          console.log('WebSocket connected, subscribing to signals topic');
          stompClient.subscribe(`/topic/signals/${currentVehicleId}`, (message: { body: string }) => {
            try {
              const parsedMessage = JSON.parse(message.body);
              console.log('Received signal message:', parsedMessage);
              setNetworkStrength(parsedMessage.signalStrength || 'NA');
            } catch (error) {
              console.error('Error parsing WebSocket message:', error);
            }
          });
        },
        onStompError: (frame) => {
          console.error('WebSocket error:', frame);
        },
        onDisconnect: () => {
          console.log('WebSocket disconnected');
        },
      });

      stompClient.activate();

      return () => {
        console.log('🧹 Cleaning up WebSocket connection');
        stompClient.deactivate();
      };
    }
  }, [open, type, trip?.vehicleRegNo, trip?.vehicleId]);

  const getNetworkStyle = (strength: string) => {
    const normalized = strength?.trim().toLowerCase();
    switch (normalized) {
      case 'excellent':
        return { bgColor: 'rgba(39, 174, 96, 0.15)', textColor: '#219653' }; // Green
      case 'medium':
        return { bgColor: 'rgba(255, 193, 7, 0.15)', textColor: '#FFC107' }; // Yellow
      case 'low':
        return { bgColor: 'rgba(244, 67, 54, 0.15)', textColor: '#F44336' }; // Red
      default:
        return { bgColor: 'rgba(189, 189, 189, 0.15)', textColor: '#9E9E9E' }; // Grey
    }
  };

  const strength = networkStrength;
  const { bgColor, textColor } = getNetworkStyle(strength);

  const {
    data: liveStreamData,
    error: liveStreamError,
    loading: liveStreamLoading,
    fetchData: fetchLiveStreamData,
    resetData: resetLiveStreamData,
    errorStatus: liveStreamErrorStatus,
  } = useAxios<{ data: CameraProps[]; message: string }>(
    `${API_ENDPOINTS.dashboard.LIVE_STREAM}${
      !isDash ? trip?.vehicleRegNo || vehicleId : trip?.vehicleId || vehicleId
    }`,
    'GET',
  );

  const {
    data: liveStreamStopData,
    error: liveStreamStopError,
    loading: liveStreamStopLoading,
    fetchData: fetchliveStreamStopData,
    resetData: resetliveStreamStopData,
    errorStatus: liveStreamStopErrorStatus,
  } = useAxios<{ data: CameraProps[]; message: string }>(
    `${API_ENDPOINTS.dashboard.STOP_LIVE_STREAMING}${!isDash ? trip?.vehicleRegNo : trip?.vehicleId}`,
    'GET',
  );

  console.info({ liveStreamError }, { liveStreamData });

  const {
    data: demoVideoUrls,
    error: demoVideoUrlsError,
    loading: demoVideoUrlsLoading,
    fetchData: demoVideoUrlsRequestData,
  } = useAxios<any>(`${API_ENDPOINTS.sdCard.DEMO_LIVE_STREAMING}/demo-device`, 'GET');

  useEffect(() => {
    if (type === 'live') {
      resetLiveStreamData();
      setDeviceOffline({ show: false, message: '' }); // Reset offline state
      setLiveVideo([]);
      setTotalVideos([]);
      prevData.current = 0;
    }
  }, [open, trip?.vehicleRegNo, type]);

  let menuOptions: {
    name: string;
    click?: (cameraName: string) => void;
  }[] = [];

  if (Array.isArray(liveStreamData?.data) && liveStreamData.data.length > 0) {
    menuOptions = liveStreamData.data.map((item) => {
      return {
        name: item.cameraName,
        onClick: () => handleCameraSelect(item.cameraName),
      };
    });
  } else if (cameraInfo && type == 'alert') {
    loadingRef.current = false;
    menuOptions = cameraInfo.map((item) => {
      return {
        name: item.cameraName,
        onClick: () => handleCameraSelect(item.cameraName),
      };
    });
  }

  const handleCameraSelection = (name: string) => {
    if (type == 'alert') {
      if (name == 'All') {
        setSelected(0);
        if (totalVideos.length !== cameraInfo.length) setTotalVideos(cameraInfo);
      } else if (name == '2 Cam') {
        setSelected(2);
        const filterData = cameraInfo.filter((item, index) => index < 2);
        console.info(filterData, 'filterData yashxx', filterData == totalVideos);
        if (totalVideos.length !== filterData.length) setTotalVideos(filterData);
      } else if (name == '4 Cam') {
        setSelected(4);
        const filterData = cameraInfo.filter((item, index) => index < 4);
        if (totalVideos.length !== filterData.length) setTotalVideos(filterData);
      } else {
        setSelected(1);
      }
    } else {
      if (name == 'All') {
        //console.info('live camera setting up', liveVideo);
        setSelected(0);
        if (totalVideos.length !== liveVideo.length) {
          setTotalVideos(liveVideo);
        }
      } else if (name == '2 Cam') {
        setSelected(2);
        const filterData = liveVideo.filter((item, index) => index < 2);
        if (totalVideos.length !== filterData.length) setTotalVideos(filterData);
      } else if (name == '4 Cam') {
        setSelected(4);
        const filterData = liveVideo.filter((item, index) => index < 4);
        if (totalVideos.length !== filterData.length) setTotalVideos(filterData);
      } else {
        setSelected(1);
      }
    }
  };

  const handleCalling = () => {
    setCallModalOpen(true);
  };

  const handleCameraSelect = (name: string) => {
    // console.info("name", name);
  };

  useEffect(() => {
    if (cameraInfo && type == 'alert') {
      setTotalVideos(cameraInfo);
    }
    if (selectedCameraName != '') {
      const filterData = cameraInfo.filter((item) => item.cameraName === selectedCameraName);
      //console.info(filterData, 'filterData');

      setTotalVideos(filterData);
    }
  }, [cameraInfo, selectedCameraName]);

  useEffect(() => {
    //console.info('live feed modal open', trip, trip?.vehicleRegNo != "", type == "live", open);

    if (trip?.vehicleRegNo != '' && type == 'live' && open) {
      if (user?.id === '685e1eec79b388464c5cd08e') {
        if (!demoVideoUrlsLoading) {
          demoVideoUrlsRequestData();
        }
      } else {
        if (!liveStreamLoading) {
          fetchLiveStreamData();
        }
      }
      loadingRef.current = true;
      //console.info('live feed api calling');
    }
  }, [trip?.vehicleRegNo, open]);

  const cameraUrls: any[] = useMemo(() => {
    if (!demoVideoUrls?.data) return [[]]; // Return an array with one empty array
    const filtered = demoVideoUrls.data.filter((item: any) => ['ADAS', 'DMS'].includes(item.cameraName));

    const mapped = filtered.map((item: any) => ({
      cameraId: item.cameraId.replace(/^0+/, ''),
      cameraName: item.cameraName,
      playBackUrl: item.playBackUrl,
    }));

    return [mapped]; // Wrap the mapped array in another array
  }, [JSON.stringify(demoVideoUrls?.data ?? [])]);

  useEffect(() => {
    let timeout: NodeJS.Timeout | undefined;

    const shouldProcess = Array.isArray(liveStreamData?.data) && type === 'live' && open;
    //console.info("live stream data processing ", trip, shouldProcess, totalVideos, liveVideo);

    if (shouldProcess) {
      const streams = liveStreamData.data;
      const streamsWithoutPlayBack = streams.filter(
        (item: any) => !item.playBackUrl || item.playBackUrl.trim() === '',
      );

      const streamsWithPlayBack = streams.filter(
        (item: any) => item.playBackUrl && item.playBackUrl.trim() !== '',
      );

      const hasStreams = streams.length > 0;
      const allStreamsReady = streamsWithoutPlayBack.length === 0 && streams.length > 0;

      console.info(
        'live Streams with playBackUrl:',
        streamsWithPlayBack.length,
        prevData.current != streamsWithPlayBack.length,
      );

      if (streamsWithPlayBack.length > 0 && prevData.current != streamsWithPlayBack.length) {
        prevData.current = streamsWithPlayBack.length;

        setTotalVideos([...streamsWithPlayBack]);
        setLiveVideo([...streamsWithPlayBack]);
        loadingRef.current = false;
        setFetchTimedOut(false);
      }

      const currentTime = Date.now();
      const timeElapsed = currentTime - fetchStartTimeRef.current;

      if (timeElapsed >= maxFetchDuration) {
        console.info('2 minute timeout reached, stopping live stream and showing message');
        setFetchTimedOut(true);
        loadingRef.current = false;

        // Stop the live stream
        if (!liveStreamStopLoading) {
          fetchliveStreamStopData();
        }

        // Clear any pending timeouts
        if (timeout) {
          clearTimeout(timeout);
        }
        return;
      }

      // Retry fetching if not all streams are ready and last error is NOT 500
      if (!allStreamsReady && !fetchTimedOut) {
        if (liveStreamErrorStatus !== '500') {
          timeout = setTimeout(() => {
            //console.info("Retrying fetchLiveStreamData...");
            if (!liveStreamLoading) {
              fetchLiveStreamData();
            }
          }, 2000);
        } else {
          loadingRef.current = false;
        }
      } else {
        // ✅ All streams are ready, no need to retry further
        loadingRef.current = false;
        //console.info("All streams are ready, stopping retries.");
      }
    }

    if (
      liveStreamData?.message == 'Device is offline' ||
      liveStreamData?.message == 'Please try again to reconnect'
    ) {
      console.info('device offline', liveStreamData?.message);

      loadingRef.current = true;
      setDeviceOffline({ show: true, message: liveStreamData?.message });
    }

    // Cleanup the timeout on component unmount or dependency change
    return () => {
      if (timeout) {
        clearTimeout(timeout);
        //console.info("Cleanup: timeout cleared");
      }
    };
  }, [liveStreamData, type, open, loadingRef.current, fetchTimedOut]);

  useEffect(() => {
    if (type === 'live' && open && trip?.vehicleRegNo && !fetchTimedOut) {
      fetchStartTimeRef.current = Date.now();
      setFetchTimedOut(false);
    }
  }, [type, open, trip?.vehicleRegNo, fetchTimedOut]);

  // Add this useEffect to reset timeout state when modal closes
  useEffect(() => {
    if (!open) {
      setFetchTimedOut(false);
      // Clear auto-close timer when modal is closed
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current);
        autoCloseTimerRef.current = null;
      }
    }
  }, [open]);

  // Auto-close live feed after 2 minutes for live stream type only
  useEffect(() => {
    if (open && type === 'live' && trip?.vehicleRegNo) {
      console.info('⏱️ Starting 2-minute auto-close timer for live feed');
      liveStreamOpenTimeRef.current = Date.now();

      // Clear any existing timer first
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current);
      }

      // Set new auto-close timer
      autoCloseTimerRef.current = setTimeout(() => {
        console.info('⏱️ 2-minute timer reached - auto-closing live feed');
        handleAutoClose();
      }, AUTO_CLOSE_DURATION);

      // Cleanup function
      return () => {
        if (autoCloseTimerRef.current) {
          clearTimeout(autoCloseTimerRef.current);
          autoCloseTimerRef.current = null;
        }
      };
    }
  }, [open, type, trip?.vehicleRegNo]);

  const { rowArr, halfLength } = useMemo(() => {
    if (totalVideos.length === 0) return { rowArr: [], halfLength: 0 };

    let videos = [...totalVideos];

    // Ensuring even number of videos for grid layout
    if (videos.length > 2 && videos.length % 2 !== 0) {
      videos.push({
        cameraId: '',
        cameraName: '',
        playBackUrl: '',
      });
    }

    const half = Math.ceil(videos.length / 2);
    const firstRow = videos.slice(0, half);
    const secondRow = videos.slice(half);

    let rows: CameraProps[][];

    if (videos.length === 2) {
      rows = [videos];
    } else {
      rows = secondRow.length > 0 ? [firstRow, secondRow] : [firstRow];
    }

    return { rowArr: rows, halfLength: half };
  }, [totalVideos]);

  useEffect(() => {
    if (!open) return; // Wait for modal to open

    const timer = setTimeout(() => {
      if (videoContainerRef.current) {
        const containerHeight = videoContainerRef.current.offsetHeight;
        const containerWidth = videoContainerRef.current.offsetWidth;

        const rowHeight = rowArr.length === 2 ? containerHeight / 2 - 16 : containerHeight - 16;

        const rowWidth =
          totalVideos.length === 2
            ? containerWidth / 2 - 16
            : containerWidth / halfLength - halfLength * 8 - 8;

        setDimension({ rowHeight, rowWidth });
      }
    }, 100); // Add small delay to ensure DOM is painted

    return () => clearTimeout(timer);
  }, [open, rowArr.length, totalVideos.length, halfLength]);

  useEffect(() => {
    if (Activelist && windowFullScreen) {
      const containerHeight = window.innerHeight;
      const containerWidth = window.innerWidth;
      setDimension({ rowHeight: containerHeight, rowWidth: containerWidth });
    } else if (Activelist && !windowFullScreen) {
      setTimeout(() => {
        if (videoContainerRef.current) {
          const containerHeight = videoContainerRef.current.offsetHeight;
          const containerWidth = videoContainerRef.current.offsetWidth;

          const rowHeight = rowArr.length === 2 ? containerHeight / 2 - 16 : containerHeight - 16;

          const rowWidth =
            totalVideos.length === 2
              ? containerWidth / 2 - 16
              : containerWidth / halfLength - halfLength * 8 - 8;

          setDimension({ rowHeight, rowWidth });
        }
      }, 200);
    }
  }, [Activelist, windowFullScreen]);

  const handleCameraDropSelect = (cameraName: string, option: { name: string }) => {
    if (type == 'live') {
      const filterData = (liveStreamData?.data || [])
        .filter((item) => item.cameraName === option.name)
        .filter((item) => item.playBackUrl && item.playBackUrl.trim() !== '');
      console.info(filterData, 'filterData');

      setTotalVideos(filterData);
    } else {
      const filterData = cameraInfo.filter((item) => item.cameraName === option.name);
      setTotalVideos(filterData);
    }
    // You can do more with the selection here!
  };

  const handleRefresh = () => {
    console.info(trip?.vehicleRegNo, 'trip?.vehicleRegNo');

    if (trip?.vehicleRegNo != '' && type == 'live') {
      resetLiveStreamData();
      fetchLiveStreamData();
      prevData.current = 0;
      setFetchTimedOut(false);
      fetchStartTimeRef.current = Date.now(); // Reset the timer
    }
    loadingRef.current = true;
  };

  // Handle auto-close after 2 minutes
  const handleAutoClose = () => {
    console.info('🔴 Auto-closing live feed after 2 minutes');
    // Clear video states
    setTotalVideos([]);
    setLiveVideo([]);
    prevData.current = 0;
    setSelected(selectedCamera);
    // Call STOP_LIVE_STREAMING API
    fetchliveStreamStopData();
    // Close the modal
    onClose();
  };

  // Wrapper for onClose to clear auto-close timer when user manually closes
  const handleManualClose = () => {
    console.info('⏹️ User manually closed live feed');
    // Clear the auto-close timer
    if (autoCloseTimerRef.current) {
      clearTimeout(autoCloseTimerRef.current);
      autoCloseTimerRef.current = null;
    }
    // Clear video states before calling STOP_LIVE_STREAMING API
    setTotalVideos([]);
    setLiveVideo([]);
    prevData.current = 0;
    setSelected(selectedCamera);
    // Call STOP_LIVE_STREAMING API
    fetchliveStreamStopData();
    // Call the original onClose callback
    onClose();
  };

  const selectedOption = totalVideos.length > 0 ? totalVideos[0].cameraName : 'FCW';

  useEffect(() => {
    setActiveList(rowArr);
  }, [rowArr]);

  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const fullscreenElement =
        document.fullscreenElement ||
        document.fullscreenElement ||
        document.fullscreenElement ||
        document.fullscreenElement;
      setIsFullscreen(!!fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, [isFullscreen]);

  useEffect(() => {
    if (document.fullscreenElement) {
      console.info('We are in fullscreen!');
    } else {
      setWindowFullScreen(false);
      setFullModee(false);
      setActiveList(rowArr);
      setSingleFullMode(false);
    }
  }, [document.fullscreenElement]);

  useEffect(() => {
    if (document.fullscreenElement) {
      console.info('We are in fullscreen!');
    } else {
      setWindowFullScreen(false);
      setFullModee(false);
      setActiveList(rowArr);
      setSingleFullMode(false);
    }
  }, [document.fullscreenElement]);

  const handleFullScreen = (cameraId: string) => {
    const element = document.getElementById('stream-subcontainer');
    if (element) {
      if (document.fullscreenElement) {
        document.exitFullscreen().then(() => {
          setWindowFullScreen(false);
          setFullModee(false);
          setActiveList(rowArr);
          setSingleFullMode(false);
        });
      } else {
        element.requestFullscreen().then(() => {
          setWindowFullScreen(true);
          setFullModee(true);
          setSingleFullMode(false);
          const filterArr = Activelist.filter((item) => {
            const itemArr: CameraProps[] = item.filter((video) => {
              if (video.cameraId == cameraId) {
                return video;
              }
            });
            if (itemArr.length > 0) {
              setActiveList([itemArr]);
              return true;
            } else {
              return false;
            }
          });
        });
      }
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && document.fullscreenElement) {
        document.exitFullscreen().then(() => {
          setWindowFullScreen(false);
          setFullModee(false);
          setActiveList(rowArr);
          setSingleFullMode(false);
        });
      }
    };
    //BHNLogistics

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [rowArr]);

  const handleFullClick = (video: any) => {
    handleFullScreen(video?.cameraId);
  };
  const navigate = useNavigate();

  const handleTripReconstruction = useCallback(() => {
    if (trip?.tripId) {
      console.log('Trip ID is available:', trip.tripId);
      navigate(`/trips/reconstruction/${trip.tripId}`);
      console.log('Navigating to trip reconstruction:', trip.tripId);
    } else {
      console.log('Trip ID is not available.');
    }
  }, [trip, navigate]);

  console.info(
    'hi',
    !isCheckingVideos,
    totalVideos,
    totalVideos.length === 0,
    type === 'alert',
    cameraInfo.length === 0,
    totalVideos,
    rowArr,
    Activelist,
  );

  return (
    <>
      <Modal open={open} disableEscapeKeyDown onClose={handleManualClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: isFullscreen ? '100%' : '80%',
            height: isFullscreen ? '100%' : '80%',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: isFullscreen ? 0 : 4,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Paper className="p-4 flex flex-col gap-4 h-full rounded-lg" sx={{ padding: 0 }}>
            {/* Header Section */}
            <div className="justify-between items-center" style={{ display: isFullscreen ? 'none' : 'flex' }}>
              <div className="flex flex-col">
                <Typography variant="h6">
                  {type === `live` ? 'Live Feed Streaming ' : `${alertName} videos`}
                </Typography>
                <div className="flex gap-1">
                  <Typography variant="body2" color="secondary" fontWeight={400}>
                    {isDash ? 'Vehicle ID:' : 'Trip ID:'}
                  </Typography>
                  <Typography variant="body2">
                    {isDash ? trip?.vehicleId || 'NA' : trip?.vehicleRegNo || trip?.tripId || 'NA'}
                  </Typography>
                </div>
              </div>
              <div className="flex gap-4">
                {type == 'live' && (
                  <div className="flex items-center gap-1">
                    <Typography variant="caption" color="secondary">
                      Network
                    </Typography>
                    <Box
                      sx={{
                        backgroundColor: bgColor,
                        px: '8px',
                        py: '4px',
                        borderRadius: '8px',
                        display: 'inline-block',
                      }}
                    >
                      <Typography variant="overline" color={textColor} fontWeight={400}>
                        {strength}
                      </Typography>
                    </Box>
                  </div>
                )}
                {type == 'live' && (
                  <>
                    <CustomIconButton
                      text="Refresh"
                      icon={<img src={refresh} alt="refresh" />}
                      background="transparent"
                      variant="outlined"
                      onClick={handleRefresh}
                    />
                    <CustomIconButton
                      text="2 way Communication"
                      outline
                      padding="0px"
                      variant="contained"
                      onClick={handleCalling}
                    />
                  </>
                )}
                {type == 'alert' && (
                  <>
                    {runningHistory && (
                      <CustomIconButton
                        text="Trip Reconstruction"
                        outline
                        icon={<img src={reverse} alt="trip reconstruction" width={24} height={24} />}
                        padding="0px"
                        background="transparent"
                        variant="contained"
                        onClick={handleTripReconstruction}
                      />
                    )}
                    <CustomIconButton
                      text="Share Footage"
                      outline
                      icon={<img src={share} alt="share" width={24} height={24} />}
                      padding="0px"
                      variant="contained"
                      onClick={() => setShareModalOpen(true)}
                    />
                  </>
                )}
                <IconButton sx={{ padding: '0px' }} onClick={handleManualClose}>
                  <img src={CloseIcon} alt="CloseIcon" />
                </IconButton>
              </div>
            </div>

            {/* Video Player Grid - Ensuring max 2 rows */}
            <div
              className={`${
                isFullscreen
                  ? 'flex flex-col gap-4 overflow-y-scroll h-screen'
                  : 'flex flex-col gap-4 overflow-y-scroll h-full'
              } `}
              ref={videoContainerRef}
            >
              {((!isCheckingVideos && totalVideos.length === 0 && type === 'alert') ||
                (cameraInfo?.length === 0 && type === 'alert')) && (
                <div className="flex h-full justify-center items-center">
                  <Typography variant="body2" color="secondary" fontWeight={600} fontSize={36}>
                    Video is yet to be uploaded
                  </Typography>
                </div>
              )}
              {fetchTimedOut && (
                <div className="text-center">
                  <Typography variant="h6" color="error" gutterBottom>
                    Live Stream Timeout
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Unable to establish live stream connection.
                  </Typography>
                  <Button variant="contained" onClick={handleRefresh} sx={{ mt: 2 }}>
                    Try Again
                  </Button>
                </div>
              )}
              {loadingRef.current && user?.id !== '685e1eec79b388464c5cd08e' ? (
                <>
                  <div className="flex h-full justify-center items-center">
                    {liveStreamLoading && !fetchTimedOut && (
                      <Typography variant="h6" color="secondary">
                        Loading...
                      </Typography>
                    )}
                    {fetchTimedOut && (
                      <div className="text-center">
                        <Typography variant="h6" color="error" gutterBottom>
                          Live Stream Timeout
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Unable to establish live stream connection.
                        </Typography>
                        <Button variant="contained" onClick={handleRefresh} sx={{ mt: 2 }}>
                          Try Again
                        </Button>
                      </div>
                    )}
                    {liveStreamErrorStatus === '500' && !fetchTimedOut && (
                      <Typography variant="h6">{liveStreamError}</Typography>
                    )}
                    {/* {liveStreamErrorStatus === '500' && (
                      <Typography variant="h6">{liveStreamError}</Typography>
                    )} */}
                  </div>
                </>
              ) : user?.id === '685e1eec79b388464c5cd08e' && type === 'live' ? (
                cameraUrls &&
                cameraUrls.map((row, rowIndex) => (
                  <div
                    key={rowIndex}
                    className={`${
                      isFullscreen
                        ? 'flex flex-col justify-center items-center h-screen gap-4'
                        : 'flex justify-center items-center h-full gap-4'
                    }`}
                    style={{ maxHeight: isFullscreen ? '100vh' : dimension.rowHeight }}
                  >
                    {row.length === 0 && (
                      <Typography variant="body2" color="secondary" fontWeight={600} fontSize={36}>
                        Video will be available soon
                      </Typography>
                    )}
                    {row.map((video: any, index: any) => (
                      <>
                        <div className="relative">
                          {index == 0 && rowIndex == 0 && (
                            <div
                              className="flex gap-2 absolute"
                              style={{ top: '4px', left: '4px', zIndex: 99 }}
                            >
                              <CameraButton
                                handleClick={handleCameraSelection}
                                isSelected={selected === 0}
                                name="All"
                              />
                              <CameraButton
                                handleClick={handleCameraSelection}
                                isSelected={selected === 1}
                                name={selectedOption}
                                options={menuOptions}
                                onSelect={handleCameraDropSelect}
                              />
                              <CameraButton
                                handleClick={handleCameraSelection}
                                isSelected={selected === 2}
                                name="2 Cam"
                              />
                              <CameraButton
                                handleClick={handleCameraSelection}
                                isSelected={selected === 4}
                                name="4 Cam"
                              />
                            </div>
                          )}

                          {type == 'live' && video?.playBackUrl && video.playBackUrl.trim() !== '' && (
                            <div className="relative" id="stream-subcontainer">
                              <MP4VideoPlayer
                                key={index}
                                streamUrl={video.playBackUrl}
                                availableHeight={dimension.rowHeight}
                                availableWidth={dimension.rowWidth}
                                cameraId={video.cameraId}
                                fullMode={windowFullScreen}
                                cameraList={cameraUrls}
                                deviceId={vehicleId}
                                deviceType={trip?.deviceType}
                              />
                              <IconButton
                                color="primary"
                                className={`absolute ${windowFullScreen ? 'right-[95px]' : 'right-[45px]'}`}
                                size="small"
                                onClick={() => {
                                  handleFullClick(video);
                                }}
                                style={{
                                  bottom: windowFullScreen ? '45px' : '30px',
                                  position: 'absolute',
                                  backgroundColor: 'white',
                                  color: '#333',
                                  zIndex: 100,
                                }}
                              >
                                {windowFullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                              </IconButton>
                            </div>
                          )}
                        </div>
                      </>
                    ))}
                  </div>
                ))
              ) : (
                Activelist.map((row, rowIndex) => (
                  <div
                    key={rowIndex}
                    className={`${
                      isFullscreen
                        ? 'flex flex-col justify-center items-center h-screen gap-4'
                        : 'flex justify-center items-center h-full gap-4'
                    }`}
                    style={{ maxHeight: isFullscreen ? '100vh' : dimension.rowHeight }}
                  >
                    {row.length === 0 && (
                      <Typography variant="body2" color="secondary" fontWeight={600} fontSize={36}>
                        Video will be available soon
                      </Typography>
                    )}
                    {row.map((video, index) => (
                      <>
                        {video?.error ? (
                          <Typography
                            variant="body2"
                            color="secondary"
                            fontWeight={600}
                            fontSize={24}
                            textAlign={'center'}
                          >
                            {video?.message}
                          </Typography>
                        ) : (
                          <div className="relative">
                            {index == 0 && rowIndex == 0 && (
                              <div
                                className="flex gap-2 absolute"
                                style={{ top: '4px', left: '4px', zIndex: 99 }}
                              >
                                <CameraButton
                                  handleClick={handleCameraSelection}
                                  isSelected={selected === 0}
                                  name="All"
                                />
                                <CameraButton
                                  handleClick={handleCameraSelection}
                                  isSelected={selected === 1}
                                  name={selectedOption}
                                  options={menuOptions}
                                  onSelect={handleCameraDropSelect}
                                />
                                <CameraButton
                                  handleClick={handleCameraSelection}
                                  isSelected={selected === 2}
                                  name="2 Cam"
                                />
                                <CameraButton
                                  handleClick={handleCameraSelection}
                                  isSelected={selected === 4}
                                  name="4 Cam"
                                />
                              </div>
                            )}

                            {type == 'alert' && video?.playBackUrl && video.playBackUrl.trim() !== '' && (
                              <div className="relative" id="stream-subcontainer">
                                {video?.alertData?.gForce?.length > 0 ? (
                                  <AlertVideoPlayer
                                    key={index}
                                    streamUrl={video.playBackUrl}
                                    availableHeight={dimension.rowHeight}
                                    availableWidth={dimension.rowWidth}
                                    alertData={video?.alertData}
                                  />
                                ) : (
                                  <MP4VideoPlayer
                                    key={index}
                                    streamUrl={video.playBackUrl}
                                    availableHeight={dimension.rowHeight}
                                    availableWidth={dimension.rowWidth}
                                    deviceId={deviceId}
                                    fullMode={windowFullScreen}
                                    cameraList={Activelist}
                                    cameraId={video.cameraId}
                                    isDriverCoachingVideos={isDriverCoachingVideos}
                                    deviceType={trip?.deviceType}
                                  />
                                )}
                                <IconButton
                                  color="primary"
                                  className={`absolute ${windowFullScreen ? 'right-[95px]' : 'right-[45px]'}`}
                                  size="small"
                                  onClick={() => {
                                    handleFullClick(video);
                                  }}
                                  style={{
                                    bottom: windowFullScreen ? '45px' : '30px',
                                    position: 'absolute',
                                    backgroundColor: 'white',
                                    color: '#333',
                                    zIndex: 100,
                                  }}
                                >
                                  {windowFullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                                </IconButton>
                              </div>
                            )}
                            {type == 'live' && video?.playBackUrl && video.playBackUrl.trim() !== '' && (
                              <div className="relative" id="stream-subcontainer">
                                <VideoPlayer
                                  key={index}
                                  streamUrl={video.playBackUrl}
                                  availableHeight={dimension.rowHeight}
                                  availableWidth={dimension.rowWidth}
                                  cameraId={video.cameraId}
                                  fullMode={windowFullScreen}
                                  cameraList={Activelist}
                                  deviceId={vehicleId}
                                  deviceType={deviceType || trip?.deviceType}
                                />
                                <IconButton
                                  color="primary"
                                  className={`absolute ${windowFullScreen ? 'right-[95px]' : 'right-[45px]'}`}
                                  size="small"
                                  onClick={() => {
                                    handleFullClick(video);
                                  }}
                                  style={{
                                    bottom: windowFullScreen ? '45px' : '30px',
                                    position: 'absolute',
                                    backgroundColor: 'white',
                                    color: '#333',
                                    zIndex: 100,
                                  }}
                                >
                                  {windowFullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                                </IconButton>
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    ))}
                  </div>
                ))
              )}
            </div>
          </Paper>
        </Box>
      </Modal>
      {type == 'live' && (
        <CallingModal tripInfo={trip} open={callModalOpen} onClose={() => setCallModalOpen(false)} />
      )}
      <ShareFootageModal
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        existingVideos={Activelist.flat().map((item) => item.playBackUrl)}
        emailConfig={{
          endpoint: API_ENDPOINTS.alarm.SHARE_ALERT_DETAILS_EMAIL,
          payload: {
            deviceId: deviceId || trip?.deviceId,
            warningSortKey: trip?.warningSortKey,
          },
        }}
        onVideoCopied={() => setShareModalOpen(false)}
        onEmailSent={() => setShareModalOpen(false)}
      />
    </>
  );
};

export default LiveFeedModal;
